import type { CounterLocale } from "@/lib/i18n/config";

/**
 * Rail destinations in Hindi, keyed by the English label `lib/packs.ts` declares.
 *
 * `lib/packs.ts` stays the one list of destinations — seventeen verticals name the same `menu` id
 * "Menu", "Passes", "Treatments" and "Bed types & rent", so the label, not the id, is what carries
 * meaning. This table only answers "how does a Hindi till say that", and `scripts/check-i18n.ts`
 * fails when a label in `lib/packs.ts` (or the rail's fallback nav) has no entry here.
 *
 * Counter Hindi: the loanwords staff actually say (मेन्यू, रिपोर्ट, शिफ़्ट) over formal coinages.
 */
export const HINDI_NAV_LABELS: Record<string, string> = {
  "Bays & technicians": "बे और मैकेनिक",
  "Beds setup": "बेड सेटअप",
  "Job cards": "जॉब कार्ड",
  "Property setup": "प्रॉपर्टी सेटअप",
  "Rent & dues": "किराया और बकाया",
  "Service due": "सर्विस ड्यू",
  Accounting: "हिसाब-किताब",
  "All screens": "सभी स्क्रीन",
  Appointments: "अपॉइंटमेंट",
  Attendance: "हाज़िरी",
  "Back to counter": "काउंटर पर वापस",
  Batches: "बैच",
  "Bay schedule": "बे शेड्यूल",
  "Bed types & rent": "बेड के प्रकार और किराया",
  "Beds & residents": "बेड और निवासी",
  Bookings: "बुकिंग",
  Catalog: "कैटलॉग",
  "Check-in": "चेक-इन",
  Classes: "क्लासें",
  Counter: "काउंटर",
  "Courses & fees": "कोर्स और फ़ीस",
  Customers: "ग्राहक",
  // Reward Desk rail (W6).
  Customer: "ग्राहक",
  Rewards: "रिवॉर्ड",
  Today: "आज",
  "Day calendar": "दिन का कैलेंडर",
  "Day grid": "दिन की ग्रिड",
  "Day-use grid": "डे-यूज़ ग्रिड",
  "Door access": "दरवाज़ा एक्सेस",
  "Door check-in": "दरवाज़े पर चेक-इन",
  "Fast counter": "फ़ास्ट काउंटर",
  "Floor map": "फ़्लोर मैप",
  "Floor plan": "फ़्लोर प्लान",
  "Frames & lenses": "फ़्रेम और लेंस",
  "Front desk": "फ़्रंट डेस्क",
  "GST invoice": "GST इनवॉइस",
  "Group dashboard": "ग्रुप डैशबोर्ड",
  Guests: "मेहमान",
  "Home collection": "घर से सैंपल",
  Housekeeping: "हाउसकीपिंग",
  "Meeting rooms": "मीटिंग रूम",
  Members: "सदस्य",
  Menu: "मेन्यू",
  Metrics: "आँकड़े",
  "OPD queue": "OPD कतार",
  "Online booking": "ऑनलाइन बुकिंग",
  "Order & checkout": "ऑर्डर और बिलिंग",
  Orders: "ऑर्डर",
  "Org & access": "संगठन और एक्सेस",
  Passes: "पास",
  Patients: "मरीज़",
  Plans: "प्लान",
  Players: "खिलाड़ी",
  Procedures: "प्रोसीजर",
  "Rates & rooms": "रेट और कमरे",
  Reports: "रिपोर्ट",
  Residents: "निवासी",
  "Rewards & returns": "रिवॉर्ड और रिटर्न",
  "Sample board": "सैंपल बोर्ड",
  "Seat plans": "सीट प्लान",
  "Self-booking page": "खुद बुकिंग पेज",
  "Services & parts": "सर्विस और पार्ट्स",
  Services: "सेवाएँ",
  "Session console": "सेशन कंसोल",
  Sessions: "सेशन",
  Settings: "सेटिंग्स",
  "Shift & drawer": "शिफ़्ट और कैश ड्रॉअर",
  Shifts: "शिफ़्ट",
  "Slot booking": "स्लॉट बुकिंग",
  "Split · merge": "बाँटें · जोड़ें",
  Students: "छात्र",
  "Tests & panels": "टेस्ट और पैनल",
  "Today's list": "आज की सूची",
  Treatments: "ट्रीटमेंट",
  "Turfs & rates": "टर्फ़ और रेट",
  Vehicles: "गाड़ियाँ",
};

/** A rail label in the till's language. An untranslated label keeps its English words. */
export function navLabel(label: string, locale: CounterLocale): string {
  return locale === "hi-IN" ? (HINDI_NAV_LABELS[label] ?? label) : label;
}
